import type { PeterIssue } from "../api/peterHandoff";

/** `<findingId>:<base>:r<regressedCount>`, with `:a<attempt>` when a finding
 *  is filed again after a run that landed nothing. */
const BOB_REF = /^(\d+):(.+):r(\d+)(?::a(\d+))?$/;

export interface BobRef {
  findingId: string;
  base: string;
  regressedCount: number;
  attempt: number;
}

export function parseBobRef(ref: unknown): BobRef | null {
  if (typeof ref !== "string") return null;
  const m = BOB_REF.exec(ref);
  if (!m) return null;
  return { findingId: m[1]!, base: m[2]!, regressedCount: Number(m[3]), attempt: m[4] === undefined ? 1 : Number(m[4]) };
}

/** `pushed`: done with neither a pull request nor a reason, so the pull
 *  request is not open yet. `came_back`: a done issue for an earlier occurrence,
 *  so the finding regressed since Peter's fix. */
export type PeterState =
  | { kind: "none" }
  | {
    kind: "open" | "working" | "stopped" | "no_pr" | "no_change" | "push_failed" | "crashed" | "pushed" | "came_back";
    issue: PeterIssue;
  }
  | { kind: "pr"; issue: PeterIssue; url: string | null };

/** Whether the daemon files a new attempt (`planBobImport`): an errored or done
 *  issue that landed no commit and no pull request. */
export function canRefile(issue: PeterIssue): boolean {
  return (issue.status === "error" || issue.status === "done") && !issue.commit_sha && !issue.pr_url;
}

const OUTCOME_STATES: Record<string, "no_change" | "push_failed" | "crashed"> = {
  nothing_to_change: "no_change",
  push_failed: "push_failed",
  crashed: "crashed",
};

/** `pr_url` as a link, or null. Only https on github.com: a stored
 *  `javascript:` url would run on click. */
export function safePrUrl(raw: unknown): string | null {
  if (typeof raw !== "string" || raw === "") return null;
  let u: URL;
  try { u = new URL(raw); } catch { return null; }
  if (u.protocol !== "https:" || u.hostname !== "github.com" || u.username !== "" || u.password !== "") return null;
  return u.href;
}

/** The newest issue filed for this finding. Ids grow, so the largest is the newest. */
export function newestIssueFor(findingId: number, issues: readonly PeterIssue[]): PeterIssue | null {
  let best: PeterIssue | null = null;
  for (const issue of issues) {
    if (issue.source !== "bob") continue;
    const ref = parseBobRef(issue.source_ref);
    if (ref === null || ref.findingId !== String(findingId)) continue;
    if (best === null || issue.id > best.id) best = issue;
  }
  return best;
}

export function peterStateFor(
  finding: { id: number; regressed_count?: number | null },
  issues: readonly PeterIssue[],
): PeterState {
  const issue = newestIssueFor(finding.id, issues);
  if (issue === null) return { kind: "none" };
  switch (issue.status) {
    case "working":
      return { kind: "working", issue };
    case "paused":
      return { kind: "stopped", issue };
    case "error": {
      const outcome = typeof issue.run_outcome === "string" ? OUTCOME_STATES[issue.run_outcome] : undefined;
      return { kind: outcome ?? "stopped", issue };
    }
    case "done": {
      const ref = parseBobRef(issue.source_ref);
      const regressed = typeof finding.regressed_count === "number" ? finding.regressed_count : 0;
      if (ref !== null && ref.regressedCount < regressed) return { kind: "came_back", issue };
      // An unlinkable `pr_url` still means a pull request exists.
      if (typeof issue.pr_url === "string" && issue.pr_url) return { kind: "pr", issue, url: safePrUrl(issue.pr_url) };
      const outcome = typeof issue.run_outcome === "string" ? OUTCOME_STATES[issue.run_outcome] : undefined;
      if (outcome) return { kind: outcome, issue };
      if (typeof issue.pr_reason === "string" && issue.pr_reason) return { kind: "no_pr", issue };
      return { kind: "pushed", issue };
    }
    default:
      // "open", and any status a later Peter adds.
      return { kind: "open", issue };
  }
}

/** Add the issues a filing created, keeping the known row for an id both hold. */
export function mergeFiled(known: readonly PeterIssue[], created: readonly PeterIssue[]): PeterIssue[] {
  const ids = new Set(known.map((i) => i.id));
  return [...known, ...created.filter((i) => !ids.has(i.id))];
}
